import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectConfig } from '@app/redux';
import {
  selectAddOns,
  selectCurrentEasyCareProduct,
  selectCurrentShopAddon,
  setMissingSelection,
} from '@app/redux/addOns';

import { ProductType } from '@mfe/shared/schema-types';

import { useShopRedirectIfNoSelection } from '../../hooks/use-redirect-to-shop';
import AddonCart from '../AddonCart';
import AEMFragment from './aem-fragment';
import { EasyCareSelector } from './easy-care-selector';

import styles from '../../styles/grouped-addon.module.scss';

type GroupedAddonFlowProps = {
  handleBack: () => void;
  handleContinue: () => void;
  isDesktop?: boolean;
  continueButtonLabel: string;
};

const GroupedAddonContent = ({ handleBack, handleContinue, isDesktop, continueButtonLabel }: GroupedAddonFlowProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');
  const missingSelectionRef = useRef<HTMLDivElement>(null);
  const currentShopAddon = useSelector(selectCurrentShopAddon);
  const chosenEasyCareProduct = useSelector(selectCurrentEasyCareProduct);
  const { showEasyCareDisclaimers } = useSelector(selectConfig);

  const { availableAddons } = useSelector(selectAddOns);

  useEffect(() => {
    return () => {
      dispatch(setMissingSelection(false));
    };
  }, [dispatch]);

  useShopRedirectIfNoSelection();

  if (!currentShopAddon) {
    return null;
  }

  return (
    <div className={styles['page-content']}>
      <div className={styles['left-card']}>
        {availableAddons.loading ? (
          <div className={styles['wrapper']}>
            <div className={styles['spinnerWrapper']}>
              <Spinner description={t('loading.description')} />
            </div>
          </div>
        ) : (
          <EasyCareSelector missingSelectionRef={missingSelectionRef} currentShopAddon={currentShopAddon} />
        )}
        <AEMFragment
          showEasyCareDisclaimers={showEasyCareDisclaimers}
          disableAction={availableAddons.loading}
          handleBack={handleBack}
          handlePrimaryAction={handleContinue}
          continueButtonLabel={continueButtonLabel}
        />
      </div>
      {isDesktop && (
        <AddonCart
          isSubmitDisabled={availableAddons.loading}
          handleClick={handleContinue}
          addon={currentShopAddon}
          submitButtonLabel={continueButtonLabel}
          selectedEasyCareProduct={chosenEasyCareProduct as ProductType}
        />
      )}
    </div>
  );
};

export default GroupedAddonContent;
